import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Monitor, Upload, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { usePlatformAccess } from './platformAccess';
import { when } from './PlatformConsole';

const mb = (n) => `${Math.round((n || 0) / 1024 / 1024)} MB`;
const guessVersion = (name) => (name.match(/(\d+\.\d+\.\d+)/) || [])[1] || '';
const newer = (a, b) => {
  const pa = a.split('.').map(Number); const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i += 1) if (pa[i] !== pb[i]) return pa[i] > pb[i];
  return false;
};

const putChunks = async (file, uploadId, chunkBytes, onProgress) => {
  const total = Math.ceil(file.size / chunkBytes);
  for (let i = 0; i < total; i += 1) {
    const blob = file.slice(i * chunkBytes, Math.min((i + 1) * chunkBytes, file.size));
    for (let attempt = 1; ; attempt += 1) {
      try {
        await api.put(`/platform/desktop-app/uploads/${uploadId}/chunks/${i}`, blob,
          { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120000 });
        break;
      } catch (e) {
        if (attempt >= 3 || e?.response?.status === 400) throw e;
      }
    }
    onProgress(Math.round(((i + 1) / total) * 95));
  }
};

export const PlatformDesktopRelease = () => {
  const owner = !!usePlatformAccess()?.owner;
  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get('/platform/desktop-app').then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = data?.current;
  const validVersion = /^\d+\.\d+\.\d+$/.test(version);
  const tooOld = validVersion && current && !newer(version, current.version);

  const pick = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (f) setVersion(guessVersion(f.name));
  };

  const upload = async () => {
    setProgress(0);
    try {
      const { data: start } = await api.post('/platform/desktop-app/uploads',
        { version, size: file.size, filename: file.name });
      await putChunks(file, start.upload_id, start.chunk_bytes, setProgress);
      await api.post(`/platform/desktop-app/uploads/${start.upload_id}/finish`, {}, { timeout: 300000 });
      setProgress(100);
      toast.success(`Version ${version} is now the download in Settings`);
      setFile(null); setVersion('');
      if (inputRef.current) inputRef.current.value = '';
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed — please try again');
    }
    setProgress(null);
  };

  return (
    <div className="pc-card" style={{ marginBottom: '1.1rem' }} data-testid="pc-desktop-release">
      <div className="pc-card-h"><h2><Monitor className="pc-inline-icon" />Desktop installer</h2></div>
      <div className="pc-sub" data-testid="pc-desktop-current">
        {current
          ? <>Branches download <b style={{ color: '#fff' }}>version {current.version}</b> ({mb(current.size)}) · published {when(current.uploaded_at)}</>
          : 'No installer published yet — branches don\'t see the download button.'}
      </div>
      {owner && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.6rem', alignItems: 'center', marginTop: '.9rem' }}>
        <input ref={inputRef} type="file" accept=".exe" onChange={pick} disabled={progress !== null}
          className="pc-input" style={{ maxWidth: 320 }} data-testid="pc-desktop-file" />
        <input value={version} onChange={(e) => setVersion(e.target.value.trim())} placeholder="Version e.g. 3.2.0"
          className="pc-input" style={{ width: 150 }} disabled={progress !== null} data-testid="pc-desktop-version" />
        <button type="button" className="pc-btn pc-btn-primary" onClick={upload}
          disabled={!file || !validVersion || tooOld || progress !== null} data-testid="pc-desktop-upload-btn">
          {progress !== null ? <Loader2 className="h-3.5 w-3.5 inline mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 inline mr-1" />}
          {progress !== null ? `Uploading ${progress}%` : 'Publish to branches'}
        </button>
      </div>}
      {tooOld && (
        <div className="pc-muted" style={{ marginTop: '.5rem', color: '#ffb86b' }} data-testid="pc-desktop-version-error">
          Must be newer than {current.version} so no branch is downgraded.
        </div>
      )}
      {!!data?.history?.length && (
        <table className="pc-table" style={{ marginTop: '1rem' }} data-testid="pc-desktop-history">
          <tbody>
            {data.history.map((h) => (
              <tr key={h.sha256}>
                <td className="pc-mono">{h.version}</td>
                <td>{mb(h.size)}</td>
                <td className="pc-muted">{h.uploaded_by}</td>
                <td className="pc-num pc-muted">{when(h.uploaded_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
